import {
    createContext,
    useContext,
    useState,
    useRef
} from 'react';

import { v4 as uuid4 } from 'uuid';
import ReactDOM
    from 'react-dom';
import {
    CircularProgress,
    Drawer, Paper,
} from "@mui/material";
import styled from 'styled-components';

const defaultContextValue = {
    isOpen: false,
    openDrawer: (content, options = {}) => {
    },
    closeDrawer: () => {
    },
    replaceDrawer: (content, options = {}) => {
    },
    setLoading: () => {},
    loading: false,
    stack: []
}

const DrawerContext = createContext(defaultContextValue);

/**
 * MUI Drawer hook, used for displaying content in a Drawer from a component.
 *
 * @returns {{
 *   openDrawer: function(JSX.Element): void,
 *   isOpen: boolean,
 *   closeDrawer: function(): void
 *   replaceDrawer: function(JSX.Element): void
 * }}
 */
export function useDrawer() {
    return useContext(DrawerContext);
}

export const StyledDrawer = styled(Drawer).withConfig({
    shouldForwardProp: (prop) => !['maxWidth', 'top'].includes(prop) //stops react complaining about maxWidth
})`

    .MuiDrawer-paperAnchorRight {
        width: 100%;
        max-width: ${props => (props?.maxWidth == 'auto' || props.maxWidth == undefined) ? '640px' : props.maxWidth};
        transition: all 0.2s;
        top: ${props => props?.top ?? '0'};
        height: calc(100% - ${props => props?.top ?? '0'});
    }

    .MuiDrawer-paperAnchorBottom {
        width: 100%;
        max-width: ${props => (props?.maxWidth == 'auto' || props.maxWidth == undefined) ? '900px' : props.maxWidth};
        margin: 0 auto;
        background: transparent;
    }
`

export function DrawerProvider({children}) {

    //we are keeping this for backwards compatibility; the provider actually relies on the ref
    //if there are components which rely on stack (I couldn't tell but just in case)
    //this component will pass the copy of the ref to the children.
    const [stack, _setStack] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [prevTitle, setPrevTitle] = useState();
    const stackRef = useRef([]);

    const setStack = (val) => {
        if(typeof val == 'function') {
            stackRef.current = val(stackRef.current);
        } else {
            stackRef.current = val;
        }
        _setStack([...stackRef.current]);
    }

    function openDrawer(drawerContent, options = {
        autofocus: null,
        position: 'right',
        maxWidth: 'auto',
        top: '0px',
        zIndexAddition: 0,
        shouldClose: async () => true,
        onAfterClose: () => {},
        sx: {}
    }) {
        if (options?.pageTitle) {
            setPrevTitle(document.title);
            document.title = options?.pageTitle;
        }

        if(options.shouldClose == undefined) {
            options.shouldClose = async () => true;
        }

        const key = uuid4(); //just in case cache does the thing.


        setStack((stack) => {
            return [...stack, {
                content: drawerContent,
                open: true,
                options,
                loading,
                key,
            }];
        });

        return key;
    }

    function closeDrawer() {
        if (stackRef.current.length === 0) return;

        let shouldCloseProxy = async () => true;

        const stack = stackRef.current;

        (stack?.[stack.length-1].options?.shouldClose ?? shouldCloseProxy)().then(
            (val) => {
                if(val == false) return;

                if (prevTitle) {
                    document.title = prevTitle;
                }
        
                setStack((stack) => {
                    const newStack = [...stack];
                    const lastEl = newStack[newStack.length-1];
                    lastEl.open = false;
        
                    if (lastEl?.options?.onAfterClose) {
                        lastEl.options.onAfterClose();
                    }
        
                    return newStack;
                });
            }
        ).catch(
            () => {
                //swallow and do nothing.
            }
        )
    }

    function replaceDrawer(drawerContent, options) {
        const key = uuid4(); //just in case cache does the thing.

        setStack((stack) => {
            const newStack = [...stack];
            const lastItem = newStack[newStack.length-1];
            lastItem.content = drawerContent;
            lastItem.options = {...lastItem.options, ...options};

            return newStack;
        });

        return key;
    }

    function popLastDrawerItem() {
        setStack((stack) => {
            const newStack = [...stack];
            newStack.pop();
            return newStack;
        });
    }

    return (
			<DrawerContext.Provider
				value={{
					isOpen,
					openDrawer,
					closeDrawer,
                    replaceDrawer,
					loading,
					setLoading,
                    stack
				}}
			>
				{children}

            {ReactDOM.createPortal(
                <div>
                    {stackRef.current.map((s, ind) => {
                        const sxFinal = s?.options?.sx ? s.options.sx : {};

                        if (s?.options?.transparent === true) {
                            sxFinal['.MuiDrawer-paper'] = {
                                background: 'transparent',
                                boxShadow: 'none',
                            };
                        }

                        return (
                            <StyledDrawer
                                
                                key={ind}
                                disableEnforceFocus
                                disableEscapeKeyDown
                                maxWidth={s?.options?.maxWidth ?? 'auto'}
                                top={s?.options?.top ?? '0'}
                                anchor={s?.options?.position ?? 'right'}
                                open={s.open}
                                onClose={closeDrawer}
                                hideBackdrop={ind > 0}

                                sx={{
                                    //MUI Have specific ranges for their zindexes - 
                                    //https://mui.com/material-ui/customization/z-index/
                                    /**
                                        mobile stepper: 1000
                                        fab: 1050
                                        speed dial: 1050
                                        app bar: 1100
                                        drawer: 1200
                                        modal: 1300
                                        snackbar: 1400
                                        tooltip: 1500
                                     */
                                    zIndex: (s?.options?.zIndexAddition ?? 0) + 1002 + ind,
                                    '& > .MuiPaper-root': {
                                        overflowY: loading ? 'hidden' : 'auto'
                                    },
                                    ...sxFinal
                                }}
                                SlideProps={{
                                    appear: s.open,
                                    in: s.open,
                                    onExited: popLastDrawerItem
                                }}
                            >
                                {loading && (
                                    <Paper
                                        sx={{
                                            position: "absolute",
                                            top: 0,
                                            right: 0,
                                            borderRadius: 0,
                                            background: 'rgba(255, 255, 255, 0.8)',
                                            padding: "10px",
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '100%',
                                            height: '100%',
                                            pointerEvents: 'none',
                                            zIndex: 2,
                                        }}
                                    >
                                        <CircularProgress />
                                    </Paper>
                                )}
                                {s.content}
                            </StyledDrawer>
                        )
                    })}
                </div>,
                document.getElementById("drawer-portal")
            )}
        </DrawerContext.Provider>
    );
}